import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ParagraphNormal, ParagraphNormalSub } from './Paragraph';
import Divider from './Divider';
import ChevronRight from './Icons/ChevronRight';
import themeColors from '../assets/styles/themeColors';

const FullContainer = styled.button`
  outline: none;
  border: none;
  align-items: center;
  display: grid;
  gap: 10px;
  grid-template-columns: ${({ hasIcon }) =>
    hasIcon ? '40px 1fr 25px' : '1fr 25px'};
  margin: 5px 0;
  padding: 15px;
  cursor: pointer;
  background-color: ${({ active }) =>
    active ? themeColors.colorBackgroundSecondaryLight : 'transparent'};
  text-align: left;

  &:hover {
    background-color: ${themeColors.colorBackgroundSecondary};
  }

  @media only screen and (max-width: 428px) {
    grid-template-columns: 1fr 25px;
    padding: 10px 0;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: ${({ hasSubtitle }) => (hasSubtitle ? 'column' : 'row')};
  gap: ${({ hasSubtitle }) => (hasSubtitle ? '5px' : '15px')};
  margin-bottom: 5px;
`;
const IconContainer = styled.div`
  align-self: flex-start;

  @media only screen and (max-width: 428px) {
    display: none;
  }
`;
const Recommended = styled.span`
  font-size: 0.8rem;
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: 600;
  color: ${themeColors.colorTextSecondary};
  background-color: ${themeColors.colorBackgroundSecondaryLight};
  text-align: center;
  margin-left: 5px;
`;
const SubtitleContainer = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;

  p {
    @media only screen and (max-width: 500px) {
      white-space: nowrap;
    }

    @media only screen and (max-width: 428px) {
      white-space: initial;
    }
  }
`;

const FundingContainer = ({
  title,
  subtitle,
  description,
  postTitle,
  showDivider,
  onClick,
  active,
  recommended,
  Icon,
  dataCy,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <FullContainer
        onClick={onClick}
        active={active}
        hasIcon={!!Icon}
        data-cy={dataCy}
      >
        {!!Icon && (
          <IconContainer>
            <Icon />
          </IconContainer>
        )}
        <Container>
          <TitleContainer hasSubtitle={!!subtitle}>
            <ParagraphNormal
              weight={700}
              color={themeColors.colorTextSecondary}
            >
              {title}
              {!subtitle && recommended && (
                <Recommended>{t('recommend')}</Recommended>
              )}
            </ParagraphNormal>
            {!!subtitle && (
              <SubtitleContainer>
                <ParagraphNormalSub color={themeColors.colorTextOutline}>
                  {subtitle}
                  {recommended && <Recommended>{t('recommend')}</Recommended>}
                </ParagraphNormalSub>
              </SubtitleContainer>
            )}
          </TitleContainer>
          <ParagraphNormalSub>{description}</ParagraphNormalSub>
          {!!postTitle && (
            <SubtitleContainer>
              <ParagraphNormalSub color={themeColors.colorTextOutline}>
                {postTitle}
              </ParagraphNormalSub>
            </SubtitleContainer>
          )}
        </Container>
        <ChevronRight />
      </FullContainer>
      {showDivider && <Divider horizontal height="10px" />}
    </>
  );
};

FundingContainer.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  Icon: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.string,
    PropTypes.shape({ render: PropTypes.func.isRequired }),
  ]).isRequired,
  recommended: PropTypes.bool,
  subtitle: PropTypes.string,
  postTitle: PropTypes.string,
  showDivider: PropTypes.bool,
  onClick: PropTypes.func,
  active: PropTypes.bool,
  dataCy: PropTypes.string,
};

FundingContainer.defaultProps = {
  showDivider: true,
  onClick: Function.prototype,
  active: false,
  recommended: false,
};

export default FundingContainer;
